import { useCallback, useEffect, useRef } from "react";
import type { Ref } from "react";

import type { CalendarAriaOptions } from "./types-calendar";

/**
 * O `Calendar` do Prime só repassa `aria-label`/`aria-labelledby` ao `<input>` (o resto vai para a raiz).
 * Aplica `aria-describedby`, `aria-invalid` e `aria-required` direto no campo, que é onde o leitor de tela lê.
 */
export function useCalendarInputAria(
  {
    "aria-describedby": describedBy,
    "aria-invalid": invalid,
    "aria-required": required,
  }: CalendarAriaOptions,
  /** `inputRef` da tela: recebe o mesmo elemento, sem tirá-lo do DS. */
  forwardedRef?: Ref<HTMLInputElement>,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setRef = useCallback(
    (value: HTMLInputElement | null) => {
      // `inline` não tem `<input>`: o Prime então entrega o próprio `inputRef` da tela, que não é elemento.
      const node = value instanceof HTMLInputElement ? value : null;
      inputRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) (forwardedRef as { current: HTMLInputElement | null }).current = node;
    },
    [forwardedRef],
  );

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const apply = (name: string, value: string | undefined) => {
      if (value === undefined) input.removeAttribute(name);
      else input.setAttribute(name, value);
    };
    apply("aria-describedby", describedBy || undefined);
    apply("aria-invalid", invalid === undefined ? undefined : String(invalid));
    apply("aria-required", required === undefined ? undefined : String(required));
  }, [describedBy, invalid, required]);

  return setRef;
}
